type Translate = (key: string) => string;

export type ConsoleCountry = 'gb' | 'it' | 'us' | 'de' | 'nl';

export type ConsoleStep = {
  label: string;
  detailHtml: string;
};

export type ConsoleLedgerRow = {
  field: string;
  value: string;
  source: string;
  country: ConsoleCountry;
  filing: string;
};

export type ConsoleScene = {
  tab: string;
  q: string;
  steps: ConsoleStep[];
  answerHtml: string;
  ledger: ConsoleLedgerRow[];
  foot: string;
};

export const getConsoleScenes = (t: Translate): ConsoleScene[] => [
  {
    tab: t('Status and identity'),
    q: t('Is Global Data Intelligence Limited still active, and what is its registered office?'),
    steps: [
      {
        label: t('Resolved'),
        detailHtml: 'Global Data Intelligence Limited &rarr; <span class="lit">GB 09410808</span>',
      },
      { label: t('Register'), detailHtml: 'Companies House, United Kingdom' },
      { label: t('Reading'), detailHtml: 'status, company type, incorporation date, registered office' },
    ],
    answerHtml:
      'Active on the UK register. Company number <span class="m">09410808</span>,<sup>1</sup> incorporated 28 January 2015.<sup>2</sup> Registered office is Artisans&rsquo; House, 7 Queensbridge, Northampton, Northamptonshire, United Kingdom, NN4 7BF.<sup>3</sup> Registered as a private limited company.<sup>4</sup>',
    ledger: [
      { field: 'status', value: 'Active', source: 'Companies House', country: 'gb', filing: 'CS01 / 2026-01-28' },
      {
        field: 'incorporation_date',
        value: '2015-01-28',
        source: 'Companies House',
        country: 'gb',
        filing: 'IN01 / 2015-01-28',
      },
      {
        field: 'registered_office',
        value: 'Artisans’ House, 7 Queensbridge, Northampton, Northamptonshire, United Kingdom, NN4 7BF',
        source: 'Companies House',
        country: 'gb',
        filing: 'AD01 / 2024-11-04',
      },
      {
        field: 'company_type',
        value: 'Private limited company',
        source: 'Companies House',
        country: 'gb',
        filing: 'IN01 / 2015-01-28',
      },
    ],
    foot: t('Four values, four filings. Each one can be pulled from the register independently.'),
  },
  {
    tab: t('Ownership chain'),
    q: t('Who ultimately controls MERIDIAN HOLDINGS SRL, and through which entities?'),
    steps: [
      {
        label: t('Resolved'),
        detailHtml:
          'Meridian Holdings SRL &rarr; <span class="lit">IT 09441220158</span><span class="smp">SAMPLE</span>',
      },
      { label: t('Traversing'), detailHtml: 'Shareholder registers, three hops, two jurisdictions' },
      { label: t('Reading'), detailHtml: 'Holding percentages, share classes, control declarations' },
    ],
    answerHtml:
      'Control resolves in three hops. <span class="m">MERIDIAN HOLDINGS SRL</span> is 74% held by <span class="m">MERIDIAN GROUP BV</span>,<sup>1</sup> which is 100% held by <span class="m">ACME CAPITAL PARTNERS LP</span>.<sup>2</sup> The Dutch UBO register names one natural person above 25%.<sup>3</sup> The remaining 26% sits with two holders below the disclosure threshold.<sup>4</sup>',
    ledger: [
      {
        field: 'hop_1_holding',
        value: '74.00%',
        source: 'Registro Imprese',
        country: 'it',
        filing: 'AS-2025-118 / 2025-06-14',
      },
      {
        field: 'hop_2_holding',
        value: '100.00%',
        source: 'KVK Handelsregister',
        country: 'nl',
        filing: 'SH-4471 / 2025-09-02',
      },
      {
        field: 'ubo_declared',
        value: 'yes, above 25%',
        source: 'KVK UBO register',
        country: 'nl',
        filing: 'UBO-0912 / 2025-09-02',
      },
      {
        field: 'residual_holders',
        value: 'below threshold',
        source: 'Registro Imprese',
        country: 'it',
        filing: 'AS-2025-118 / 2025-06-14',
      },
    ],
    foot: t('SAMPLE entities. Every hop returns the register and filing that recorded the holding.'),
  },
  {
    tab: t('Latest financials'),
    q: t('Show me the latest financial statements for Revolut.'),
    steps: [
      {
        label: t('Resolved'),
        detailHtml:
          'Revolut &rarr; four registered entities in GB. Group accounts filed by Revolut Group Holdings Ltd, <span class="lit">GB 12743269</span>',
      },
      { label: t('Register'), detailHtml: 'Companies House, United Kingdom' },
      { label: t('Reading'), detailHtml: 'Annual accounts, year ended 31 December 2025' },
    ],
    answerHtml:
      'Latest filed accounts cover the year ended 31 December 2025.<sup>1</sup> Revenue <span class="m">£4.5bn</span>, from <span class="m">£3.1bn</span> the year before.<sup>2</sup> Profit before tax <span class="m">£1.7bn</span>, from <span class="m">£1.1bn</span>.<sup>3</sup> Filed by Revolut Group Holdings Ltd, <span class="m">12743269</span>, the group holding company, not Revolut Ltd, <span class="m">08804411</span>, which files separately.<sup>4</sup>',
    ledger: [
      { field: 'period_end', value: '2025-12-31', source: 'Companies House', country: 'gb', filing: 'AA / FY2025' },
      { field: 'revenue', value: '£4.5bn', source: 'Companies House', country: 'gb', filing: 'AA / FY2025' },
      {
        field: 'profit_before_tax',
        value: '£1.7bn',
        source: 'Companies House',
        country: 'gb',
        filing: 'AA / FY2025',
      },
      { field: 'filing_entity', value: '12743269', source: 'Companies House', country: 'gb', filing: 'AA / FY2025' },
    ],
    foot: t('One brand name, four registered entities. The ledger names which one actually filed.'),
  },
  {
    tab: t('Group structure'),
    q: t('Show me all the Mastercard subsidiaries worldwide.'),
    steps: [
      {
        label: t('Resolved'),
        detailHtml: 'Mastercard &rarr; Mastercard Incorporated, Delaware, <span class="lit">CIK 1141391</span>',
      },
      { label: t('Traversing'), detailHtml: 'Corporate tree outward from the parent, register by register' },
      { label: t('Reading'), detailHtml: 'Registered names, numbers, jurisdictions, incorporation dates' },
    ],
    answerHtml:
      'The parent is Mastercard Incorporated, Delaware, <span class="m">CIK 1141391</span>.<sup>1</sup> Its own 10-K exhibit names <span class="m">16</span> significant subsidiaries across Delaware, the United Kingdom, Belgium, Singapore and the United Arab Emirates.<sup>2</sup> The exhibit omits every entity the parent treats as insignificant, so it is a floor rather than the group. Regis returns the registered entities themselves: Mastercard Europe Services Limited sits on the UK register as <span class="m">09210818</span>.<sup>3</sup>',
    ledger: [
      {
        field: 'group_parent',
        value: 'Mastercard Incorporated',
        source: 'SEC EDGAR',
        country: 'us',
        filing: '10-K EX-21 / 2026-02-11',
      },
      {
        field: 'significant_subsidiaries',
        value: '16',
        source: 'SEC EDGAR',
        country: 'us',
        filing: '10-K EX-21 / 2025-12-31',
      },
      {
        field: 'gb_entity',
        value: '09210818',
        source: 'Companies House',
        country: 'gb',
        filing: 'IN01 / 2014-09-09',
      },
      {
        field: 'jurisdictions_named',
        value: 'US, GB, BE, SG, AE',
        source: 'SEC EDGAR',
        country: 'us',
        filing: '10-K EX-21 / 2025-12-31',
      },
    ],
    foot: t('A parent discloses what it must. A register records what exists. These are not the same list.'),
  },
  {
    tab: t('Original filings'),
    q: t('Give me the original filings for Siemens AG.'),
    steps: [
      {
        label: t('Resolved'),
        detailHtml:
          'Siemens Aktiengesellschaft &rarr; two register entries, <span class="lit">HRB 12300</span> Berlin and <span class="lit">HRB 6684</span> München',
      },
      { label: t('Register'), detailHtml: 'Handelsregister. Amtsgericht Charlottenburg and Amtsgericht München' },
      { label: t('Retrieving'), detailHtml: 'Documents as filed, alongside the fields extracted from them' },
    ],
    answerHtml:
      'Siemens Aktiengesellschaft holds two register entries, because its registered seat is legally both Berlin and Munich: <span class="m">HRB 12300</span> at Amtsgericht Charlottenburg<sup>1</sup> and <span class="m">HRB 6684</span> at Amtsgericht München.<sup>2</sup> Both are returned. The filings come back as the register issued them, in the language they were filed in: the current extract, the articles of association<sup>3</sup> and the annual accounts published in the Bundesanzeiger.<sup>4</sup>',
    ledger: [
      {
        field: 'register_entry',
        value: 'HRB 12300',
        source: 'Amtsgericht Charlottenburg',
        country: 'de',
        filing: 'AD / current extract',
      },
      {
        field: 'register_entry',
        value: 'HRB 6684',
        source: 'Amtsgericht München',
        country: 'de',
        filing: 'AD / current extract',
      },
      {
        field: 'articles_of_association',
        value: 'Satzung',
        source: 'Handelsregister',
        country: 'de',
        filing: 'DK / filed document',
      },
      {
        field: 'annual_accounts',
        value: 'Jahresabschluss',
        source: 'Bundesanzeiger',
        country: 'de',
        filing: 'published filing',
      },
    ],
    foot: t('The document comes back as the register issued it. The extraction sits beside it, not instead of it.'),
  },
  {
    tab: t('Market screen'),
    q: t('Show me the latest UK IT companies with EBITDA between £2m and £10m growing at least 100% year on year.'),
    steps: [
      {
        label: t('Parsed'),
        detailHtml:
          'Jurisdiction <span class="lit">GB</span>, SIC <span class="lit">62</span> and <span class="lit">63</span>, EBITDA <span class="lit">2000000–10000000</span>, YoY growth <span class="lit">&gt;=100%</span>',
      },
      { label: t('Register'), detailHtml: 'Companies House, United Kingdom' },
      {
        label: t('Checking'),
        detailHtml: 'EBITDA is not a filed line item. Testing which entities file enough to derive it',
      },
    ],
    answerHtml:
      'Sector and jurisdiction come straight from the register: SIC <span class="m">62</span> and <span class="m">63</span> cover computer programming, consultancy and information services.<sup>1</sup> Growth is measured between the two most recent filed periods.<sup>2</sup> EBITDA is not a line in UK statutory accounts. It is derived from operating profit with depreciation and amortisation added back, which needs full accounts.<sup>3</sup> Micro-entity and abridged filers do not disclose enough, so they are excluded rather than estimated.<sup>4</sup>',
    ledger: [
      { field: 'sic_codes', value: '62, 63', source: 'Companies House', country: 'gb', filing: 'SIC 2007 register' },
      {
        field: 'growth_basis',
        value: 'two most recent periods',
        source: 'Companies House',
        country: 'gb',
        filing: 'AA filings',
      },
      {
        field: 'ebitda',
        value: 'derived, not filed',
        source: 'Companies House',
        country: 'gb',
        filing: 'computed from AA',
      },
      {
        field: 'excluded',
        value: 'micro-entity, abridged',
        source: 'Companies House',
        country: 'gb',
        filing: 'coverage gap',
      },
    ],
    foot: t(
      'A screen is only as defensible as the filings under it. What cannot be computed is dropped, not estimated.',
    ),
  },
];
